import { AppointmentService } from '../../api/services/AppointmentService';
import type { Routemize_DTOs_Availability_AssigneeWorkWindowsDto } from '../../api/models/Routemize_DTOs_Availability_AssigneeWorkWindowsDto';
import type { AssigneeWorkDayLike } from '../../utils/preferredTimeRanges';

/** Which layer supplied the assignee's hours for the date. */
export type AssigneeWorkDaySource = 'Override' | 'Recurring' | 'Company';

/**
 * The assignee's effective working day for the exact-time picker: per-date availability override →
 * recurring weekly hours → company schedule (the same resolution the backend's work-hours guard uses).
 */
export interface AssigneeWorkDayVM extends AssigneeWorkDayLike {
  isWorking: boolean;
  source: AssigneeWorkDaySource;
  /** Tenant-local 'HH:mm' windows, ordered by start. */
  windows: { start: string; end: string }[];
}

const mapSource = (source?: string | null): AssigneeWorkDaySource => {
  switch (source) {
    case 'Override':
      return 'Override';
    case 'Recurring':
      return 'Recurring';
    case 'Company':
    default:
      return 'Company';
  }
};

const mapToVM = (dto: Routemize_DTOs_Availability_AssigneeWorkWindowsDto): AssigneeWorkDayVM => ({
  isWorking: dto.isWorking ?? false,
  source: mapSource(dto.source),
  windows: (dto.windows ?? []).flatMap(w =>
    w.startTime && w.endTime ? [{ start: w.startTime, end: w.endTime }] : []),
});

/**
 * Wraps the read-only endpoint that returns a team member's working windows for a date, so the exact-time
 * pickers can bound and validate against the member's own hours (ClickUp 86bcj7m6e).
 */
export class AssigneeWorkWindowsManager {
  /** @param date tenant-local calendar date, 'YYYY-MM-DD'. */
  async fetch(userId: string, date: string): Promise<AssigneeWorkDayVM> {
    const result = await AppointmentService.getApiAppAppointmentGetAssigneeWorkWindows(userId, date);
    return mapToVM(result);
  }
}

export const assigneeWorkWindowsManager = new AssigneeWorkWindowsManager();
